/**
 * Review workspace client (DESIGN.md §7.2, M1).
 *
 * Every mutation returns the whole refreshed workspace, so the stats rail and the queue can never
 * disagree with the server about what a verdict changed.
 */

import { AuthError } from './auth';
import { authorizedFetch } from './cognito';
import { AnalysisRun, BeatCluster, RhythmEpisode, StudyStatus } from './types';

const API_BASE = '/api/v1';

export interface TriageItem extends RhythmEpisode {
  priority: number;
  must_review: boolean;
}

export interface ReviewStatistics {
  analyzable_seconds: number;
  monitored_seconds?: number;
  analyzable_fraction?: number;
  hr_min?: number;
  hr_mean?: number;
  hr_max?: number;
  hr_recomputed_after_review: boolean;
  total_beats?: number;
  beat_counts: Record<string, number>;
  episode_counts: Record<string, number>;
  burden_seconds: Record<string, number>;
  burden_percent: Record<string, number | null>;
  clusters_relabelled: number;
  verification: Record<string, { total: number; reviewed: number }>;
  fully_verified: boolean;
}

export interface ReviewWorkspace {
  study_id: string;
  status: StudyStatus;
  analysis: AnalysisRun | null;
  queue: TriageItem[];
  clusters: BeatCluster[];
  statistics: ReviewStatistics | null;
  signature_blockers: string[];
  lock_held_by_self: boolean;
}

/**
 * What one review action changed (backend ReviewDeltaSchema). Actions used to return the whole
 * workspace — ~4 MB per Confirm or Reject on a real 101-hour study — and the portal then refetched the
 * full overview too. The client now merges this into what it already holds.
 */
export interface ReviewDelta {
  study_id: string;
  run_id: string | null;
  status: StudyStatus;
  episodes: TriageItem[];
  /** Changed clusters. `template_samples` is always null here: review never changes a template. */
  clusters: BeatCluster[];
  statistics: ReviewStatistics | null;
  signature_blockers: string[];
  lock_held_by_self: boolean;
}

/**
 * Merge a delta into the workspace. Queue order is kept as loaded rather than re-sorted by the
 * server's triage order, which pushes reviewed items to the bottom — so rows do not jump out from
 * under the reviewer mid-session; a fresh load re-sorts.
 */
export function applyDelta(ws: ReviewWorkspace, delta: ReviewDelta): ReviewWorkspace {
  const changed = new Map(delta.episodes.map((e) => [e.id, e]));
  const queue = ws.queue.map((item) => changed.get(item.id) ?? item);
  const known = new Set(ws.queue.map((item) => item.id));
  for (const e of delta.episodes) if (!known.has(e.id)) queue.push(e);

  const changedClusters = new Map(delta.clusters.map((c) => [c.id, c]));
  const clusters = ws.clusters.map((c) => {
    const next = changedClusters.get(c.id);
    return next ? { ...next, template_samples: c.template_samples } : c;
  });

  return {
    ...ws,
    status: delta.status,
    queue,
    clusters,
    statistics: delta.statistics,
    signature_blockers: delta.signature_blockers,
    lock_held_by_self: delta.lock_held_by_self,
  };
}

/** The overview lists non-rejected findings only; mirror that as findings change. */
export function applyDeltaToOverview(overview: Overview, delta: ReviewDelta): Overview {
  if (delta.episodes.length === 0) return overview;
  const changed = new Map(delta.episodes.map((e) => [e.id, e]));
  const toLane = (e: TriageItem) => ({
    id: e.id,
    rhythm_class: e.rhythm_class,
    onset_seconds: e.onset_seconds,
    offset_seconds: e.offset_seconds,
    status: e.status,
    is_critical: e.is_critical,
  });
  const episodes = overview.episodes.flatMap((e) => {
    const next = changed.get(e.id);
    if (!next) return [e];
    return next.status === 'REJECTED' ? [] : [toLane(next)];
  });
  const present = new Set(overview.episodes.map((e) => e.id));
  for (const e of delta.episodes) {
    if (!present.has(e.id) && e.status !== 'REJECTED') episodes.push(toLane(e));
  }
  return { ...overview, episodes };
}

export interface Overview {
  duration_seconds: number;
  hr_trend: { bin_seconds: number; bpm: (number | null)[] };
  episodes: {
    id: string;
    rhythm_class: string;
    onset_seconds: number;
    offset_seconds: number;
    status: string;
    is_critical: boolean;
  }[];
  unanalyzable_spans: [number, number][];
}

export interface BeatMarker {
  t: number;
  label: string;
  rr_ms: number | null;
  channels: number;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  // authorizedFetch already tries a silent token refresh and retries once before this ever sees
  // a 401 (cognito.ts) — a 401 here means that failed too.
  const res = await authorizedFetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    throw new AuthError(401, 'Not signed in');
  }
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') message = body.detail;
    } catch {
      /* keep the generic message */
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

const base = (studyId: string) => `/studies/${studyId}/review`;

export const fetchProgress = (studyId: string) =>
  call<import('./components/AnalysisProgress').Progress>(`${base(studyId)}/progress`);
const post = <T>(path: string, body?: unknown) =>
  call<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const fetchWorkspace = (studyId: string) => call<ReviewWorkspace>(base(studyId));

export const verdict = (
  studyId: string,
  episodeId: string,
  kind: 'confirm' | 'reject' | 'reclassify',
  rhythmClass?: string,
) =>
  post<ReviewDelta>(`${base(studyId)}/verdicts`, {
    episode_id: episodeId,
    verdict: kind,
    rhythm_class: rhythmClass,
  });

export const bulkConfirm = (studyId: string, rhythmClass: string, confirmCount: number) =>
  post<ReviewDelta>(`${base(studyId)}/bulk-confirm`, {
    rhythm_class: rhythmClass,
    confirm_count: confirmCount,
  });

/** Unlike bulkConfirm, allowed for must-review classes: acknowledging an alarm fired is not the
 * same as accepting it as a true finding (§13.1). */
export const bulkAcknowledgeCritical = (studyId: string, rhythmClass: string, confirmCount: number) =>
  post<ReviewDelta>(`${base(studyId)}/bulk-acknowledge-critical`, {
    rhythm_class: rhythmClass,
    confirm_count: confirmCount,
  });

export const relabelCluster = (studyId: string, clusterId: string, label: string, memberCount: number) =>
  call<ReviewDelta>(`${base(studyId)}/clusters/${clusterId}`, {
    method: 'PATCH',
    body: JSON.stringify({ label, confirm_member_count: memberCount }),
  });

export const selectStrip = (studyId: string, episodeId: string, selected: boolean) =>
  post<ReviewDelta>(`${base(studyId)}/episodes/${episodeId}/strip`, { selected });

export const acknowledgeCritical = (studyId: string, episodeId: string) =>
  post<ReviewDelta>(`${base(studyId)}/episodes/${episodeId}/acknowledge-critical`);

export const undo = (studyId: string) => post<ReviewDelta>(`${base(studyId)}/undo`);

export const submitForSignature = (studyId: string) => post<ReviewDelta>(`${base(studyId)}/submit`);

export const returnToReview = (studyId: string, reason: string) =>
  post<ReviewDelta>(`${base(studyId)}/return`, { reason });

export const fetchOverview = (studyId: string) => call<Overview>(`${base(studyId)}/overview`);

/**
 * Beat markers for one window.
 *
 * Takes the same `AbortSignal` the waveform fetch does. It did not, and that was the half of the
 * drag problem that went unfixed: aborting the range fetches while leaving the marker request
 * running meant a fast drag still queued one uncancellable request per intermediate window, each
 * of which cost the server a re-read of the whole beat index (DESIGN.md §13.6 S1).
 *
 * The response is revalidated rather than uncached, so panning back to a window already visited
 * is a 304 the browser answers from its own cache.
 */
export const fetchBeatMarkers = (studyId: string, start: number, end: number, signal?: AbortSignal) =>
  call<BeatMarker[]>(
    `${base(studyId)}/beats?start=${start.toFixed(3)}&end=${end.toFixed(3)}`,
    { signal },
  );

/** Reclassification targets, bound to number keys 1–9 (§7.4). */
export const RECLASSIFY_CLASSES = [
  'afib',
  'flutter',
  'svt',
  'vt',
  'pause',
  'bradycardia',
  'tachycardia',
  'av_block',
  'possible_vf',
] as const;

export const BEAT_LABEL_NAMES: Record<string, string> = {
  N: 'Normal',
  V: 'Ventricular',
  S: 'Supraventricular',
  A: 'Artifact',
  Q: 'Unclassified',
  P: 'Paced',
};

export function formatStudyTime(seconds: number): string {
  const day = Math.floor(seconds / 86400) + 1;
  const rem = seconds % 86400;
  const h = Math.floor(rem / 3600);
  const m = Math.floor((rem % 3600) / 60);
  const s = Math.floor(rem % 60);
  const hms = [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
  return `D${day} ${hms}`;
}
